import * as Sentry from '@sentry/react-router';
import type { Locale as DateFnsLocale } from 'date-fns/locale';
import { enGB as enGB_dateFns } from 'date-fns/locale/en-GB';
import type { LocaleCode } from './locales-config';
import { DEFAULT_LOCALE } from './locales-config';
import enGB from './locales/en_GB.json';
import { keysToIcuPlural } from './utils';

type LocaleBundle = {
  messages: ReturnType<typeof keysToIcuPlural<keyof typeof enGB>>;
  dateFnsLocale: DateFnsLocale;
};

const defaultBundle: LocaleBundle = { dateFnsLocale: enGB_dateFns, messages: keysToIcuPlural(enGB) };

const bundleImport = (
  messagesImport: Promise<{ default: Record<string, string> }>,
  dateFnsImport: Promise<DateFnsLocale>,
): Promise<LocaleBundle> =>
  Promise.all([messagesImport, dateFnsImport]).then(([{ default: messages }, dateFnsLocale]) => ({
    dateFnsLocale,
    messages: keysToIcuPlural(enGB, messages),
  }));

// en-GB is bundled because every other locale falls back to it for untranslated keys. The `import()` calls must stay
// literal at each entry so the bundler emits one chunk per locale.
const importers: Record<Exclude<LocaleCode, typeof DEFAULT_LOCALE>, () => Promise<LocaleBundle>> = {
  de: () =>
    bundleImport(
      import('./locales/de.json'),
      import('date-fns/locale/de').then((m) => m.de),
    ),
  'en-US': () =>
    bundleImport(
      import('./locales/en_US.json'),
      import('date-fns/locale/en-US').then((m) => m.enUS),
    ),
  fr: () =>
    bundleImport(
      import('./locales/fr.json'),
      import('date-fns/locale/fr').then((m) => m.fr),
    ),
  it: () =>
    bundleImport(
      import('./locales/it.json'),
      import('date-fns/locale/it').then((m) => m.it),
    ),
  ja: () =>
    bundleImport(
      import('./locales/ja.json'),
      import('date-fns/locale/ja').then((m) => m.ja),
    ),
  'nl-BE': () =>
    bundleImport(
      import('./locales/nl_BE.json'),
      import('date-fns/locale/nl-BE').then((m) => m.nlBE),
    ),
  'zh-CN': () =>
    bundleImport(
      import('./locales/zh_CN.json'),
      import('date-fns/locale/zh-CN').then((m) => m.zhCN),
    ),
};

const loadedBundles = new Map<LocaleCode, LocaleBundle>([[DEFAULT_LOCALE, defaultBundle]]);
const pendingLoads = new Map<LocaleCode, Promise<LocaleCode>>();

/**
 * Loads a locale's messages and date-fns locale so the synchronous getters can return them. Never rejects: resolves
 * to the locale that is actually available, which is the default locale if loading failed.
 */
export function loadLocale(locale: LocaleCode): Promise<LocaleCode> {
  if (locale === DEFAULT_LOCALE || loadedBundles.has(locale)) {
    return Promise.resolve(locale);
  }

  const pending = pendingLoads.get(locale);
  if (pending) {
    return pending;
  }

  const load = importers[locale]()
    .then((bundle) => {
      loadedBundles.set(locale, bundle);
      return locale;
    })
    .catch((error: unknown) => {
      Sentry.captureMessage(`Failed to load locale "${locale}", falling back to ${DEFAULT_LOCALE}`, {
        extra: { error },
        level: 'warning',
      });
      return DEFAULT_LOCALE;
    })
    .finally(() => pendingLoads.delete(locale));

  pendingLoads.set(locale, load);
  return load;
}

const getBundle = (locale: LocaleCode) => loadedBundles.get(locale) ?? defaultBundle;

export const getLocaleMessages = (locale: LocaleCode) => getBundle(locale).messages;

export const getDateFnsLocale = (locale: LocaleCode) => getBundle(locale).dateFnsLocale;
